import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { config } from '../../config';

const jsonHeaders = { Accept: 'application/json', 'Content-Type': 'application/json' };

// Per-user named filter presets ("saved views"), keyed by board shortId.
// Mirrors the customFieldsSlice pattern.

export const fetchSavedFilters = createAsyncThunk(
    'savedFilters/fetch',
    async (boardId, { rejectWithValue }) => {
        try {
            const res = await fetch(`${config.API_URI}/v1/users/me/saved-filters?boardId=${boardId}`, {
                method: 'GET', credentials: 'include', headers: jsonHeaders
            });
            if (!res.ok) throw new Error('Failed to load saved filters');
            const data = await res.json();
            return { boardId, savedFilters: data.savedFilters || [] };
        } catch (err) {
            return rejectWithValue(err.message);
        }
    }
);

export const createSavedFilter = createAsyncThunk(
    'savedFilters/create',
    async ({ boardId, name, config: filterConfig }, { rejectWithValue }) => {
        try {
            const res = await fetch(`${config.API_URI}/v1/users/me/saved-filters`, {
                method: 'POST', credentials: 'include', headers: jsonHeaders,
                body: JSON.stringify({ boardId, name, config: filterConfig })
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message || 'Failed to create saved filter');
            return { boardId, savedFilter: data.savedFilter };
        } catch (err) {
            return rejectWithValue(err.message);
        }
    }
);

export const updateSavedFilter = createAsyncThunk(
    'savedFilters/update',
    async ({ boardId, filterId, name, config: filterConfig }, { rejectWithValue }) => {
        try {
            const body = {};
            if (name !== undefined) body.name = name;
            if (filterConfig !== undefined) body.config = filterConfig;
            const res = await fetch(`${config.API_URI}/v1/users/me/saved-filters/${boardId}/${filterId}`, {
                method: 'PUT', credentials: 'include', headers: jsonHeaders,
                body: JSON.stringify(body)
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message || 'Failed to update saved filter');
            return { boardId, savedFilter: data.savedFilter };
        } catch (err) {
            return rejectWithValue(err.message);
        }
    }
);

export const deleteSavedFilter = createAsyncThunk(
    'savedFilters/delete',
    async ({ boardId, filterId }, { rejectWithValue }) => {
        try {
            const res = await fetch(`${config.API_URI}/v1/users/me/saved-filters/${boardId}/${filterId}`, {
                method: 'DELETE', credentials: 'include', headers: jsonHeaders
            });
            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                throw new Error(data.message || 'Failed to delete saved filter');
            }
            return { boardId, filterId };
        } catch (err) {
            return rejectWithValue(err.message);
        }
    }
);

const sortByOrder = (a, b) => (a.order ?? 0) - (b.order ?? 0);

const savedFiltersSlice = createSlice({
    name: 'savedFilters',
    initialState: {
        // Presets keyed by board shortId.
        byBoard: {},
        status: 'idle',
        error: null
    },
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchSavedFilters.fulfilled, (state, action) => {
                const { boardId, savedFilters } = action.payload;
                state.byBoard[boardId] = [...savedFilters].sort(sortByOrder);
            })
            .addCase(createSavedFilter.fulfilled, (state, action) => {
                const { boardId, savedFilter } = action.payload;
                const list = state.byBoard[boardId] || [];
                list.push(savedFilter);
                state.byBoard[boardId] = list.sort(sortByOrder);
            })
            .addCase(updateSavedFilter.fulfilled, (state, action) => {
                const { boardId, savedFilter } = action.payload;
                const list = state.byBoard[boardId] || [];
                const idx = list.findIndex(f => f._id === savedFilter._id);
                if (idx >= 0) list[idx] = savedFilter;
                state.byBoard[boardId] = list.sort(sortByOrder);
            })
            .addCase(deleteSavedFilter.fulfilled, (state, action) => {
                const { boardId, filterId } = action.payload;
                if (state.byBoard[boardId]) {
                    state.byBoard[boardId] = state.byBoard[boardId].filter(f => f._id !== filterId);
                }
            });
    }
});

export const selectBoardSavedFilters = (state, boardId) =>
    state.savedFilters.byBoard[boardId] || [];

export default savedFiltersSlice.reducer;
